import type { CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { lineMiddleY, lineStartX } from './coverGeometry';
import { rgba } from './effects/effectParams';
import { measureCoverText, type MeasuredCoverText } from './measureCoverText';

type Ctx = CanvasRenderingContext2D;
type Paint = 'fill' | 'stroke';

/** True when the browser spaces letters by itself (`ctx.letterSpacing`). */
function hasLetterSpacing(ctx: Ctx): boolean {
  return 'letterSpacing' in ctx;
}

/** One line at (x, y). With no `ctx.letterSpacing`, each character is drawn at its own place. */
function paintLine(ctx: Ctx, paint: Paint, line: string, x: number, y: number, trackingPx: number): void {
  const draw = (text: string, at: number): void => {
    if (paint === 'fill') {
      ctx.fillText(text, at, y);
    } else {
      ctx.strokeText(text, at, y);
    }
  };
  if (trackingPx === 0 || hasLetterSpacing(ctx)) {
    draw(line, x);
    return;
  }
  let at = x;
  for (const character of Array.from(line)) {
    draw(character, at);
    at += ctx.measureText(character).width + trackingPx;
  }
}

function paintLines(ctx: Ctx, paint: Paint, measured: MeasuredCoverText, box: CoverTextBox): void {
  measured.lines.forEach((line, index) => {
    const x = lineStartX(box.align, measured.boxWidth, measured.lineWidths[index] ?? 0);
    paintLine(
      ctx,
      paint,
      line,
      x,
      lineMiddleY(index, measured.lines.length, measured.lineHeightPx),
      measured.trackingPx
    );
  });
}

function clearShadow(ctx: Ctx): void {
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

/**
 * Draws one text box on a cover of `side` pixels: the wrapped lines about the centre of the box,
 * turned by its rotation, in this order: the glow, the shadow, the outline, the text. The canvas
 * shadow is not turned with the text; its offset is in the pixels of the cover.
 */
export function drawCoverText(ctx: Ctx, box: CoverTextBox, side: number): void {
  if (box.text.trim() === '' || box.opacity <= 0) {
    return;
  }
  const measured = measureCoverText(box, side, ctx);
  ctx.save();
  ctx.translate(box.x * side, box.y * side);
  ctx.rotate((box.rotationDeg * Math.PI) / 180);
  ctx.globalAlpha = box.opacity;
  ctx.font = measured.font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineJoin = 'round';
  if (hasLetterSpacing(ctx)) {
    (ctx as Ctx & { letterSpacing: string }).letterSpacing = `${measured.trackingPx}px`;
  }
  ctx.fillStyle = box.colour;

  if (box.glow !== null && box.glow.blurShare > 0) {
    ctx.shadowColor = rgba(box.glow.colour, 0.95);
    ctx.shadowBlur = box.glow.blurShare * side;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    paintLines(ctx, 'fill', measured, box);
  }
  if (box.shadow !== null) {
    ctx.shadowColor = rgba(box.shadow.colour, 0.55);
    ctx.shadowBlur = box.shadow.blurShare * side;
    ctx.shadowOffsetX = box.shadow.offsetXShare * side;
    ctx.shadowOffsetY = box.shadow.offsetYShare * side;
    paintLines(ctx, 'fill', measured, box);
  }
  clearShadow(ctx);
  if (box.outline !== null && box.outline.widthShare > 0) {
    ctx.strokeStyle = box.outline.colour;
    // The stroke sits on the edge of each letter: half of it shows outside.
    ctx.lineWidth = box.outline.widthShare * side * 2;
    paintLines(ctx, 'stroke', measured, box);
  }
  paintLines(ctx, 'fill', measured, box);
  ctx.restore();
}
